import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { logError } from '@/lib/logger';

import { API } from '@/lib/apiBase';

const ThemeContext = createContext(null);

export function useTheme() {
  return useContext(ThemeContext);
}

function hexToHSL(hexInput) {
  if (!hexInput || hexInput.length < 4) return null;
  let hex = hexInput.replace('#', '');
  if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return Math.round(h * 360) + ' ' + Math.round(s * 100) + '% ' + Math.round(l * 100) + '%';
}

const FONT_MAP = {
  'Manrope': "'Manrope', sans-serif",
  'Inter': "'Inter', sans-serif",
  'Roboto': "'Roboto', sans-serif",
  'Open Sans': "'Open Sans', sans-serif",
  'Lato': "'Lato', sans-serif",
  'Poppins': "'Poppins', sans-serif",
  'Nunito': "'Nunito', sans-serif",
  'Source Sans Pro': "'Source Sans 3', sans-serif",
  'DM Sans': "'DM Sans', sans-serif",
  'IBM Plex Sans': "'IBM Plex Sans', sans-serif",
  'Outfit': "'Outfit', sans-serif",
  'Montserrat': "'Montserrat', sans-serif",
  'Playfair Display': "'Playfair Display', serif",
  'Merriweather': "'Merriweather', serif",
  'Oswald': "'Oswald', sans-serif",
};

const loadedFonts = {};

function loadGoogleFont(fontName) {
  if (!fontName || loadedFonts[fontName]) return;
  loadedFonts[fontName] = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=' + fontName.replace(/ /g, '+') + ':wght@400;500;600;700;800;900&display=swap';
  document.head.appendChild(link);
}

function applyTheme(settings) {
  if (!settings) return;
  const root = document.documentElement;
  const body = document.body;

  const vars = [
    ['--background', settings.theme_background],
    ['--foreground', settings.theme_foreground],
    ['--card', settings.theme_card],
    ['--card-foreground', settings.theme_card_foreground],
    ['--primary', settings.theme_primary],
    ['--primary-foreground', settings.theme_primary_foreground],
    ['--secondary', settings.theme_secondary],
    ['--muted', settings.theme_muted],
    ['--muted-foreground', settings.theme_muted_foreground],
    ['--border', settings.theme_border],
    ['--input', settings.theme_border],
    ['--ring', settings.theme_primary],
    ['--accent', settings.theme_primary],
    ['--accent-foreground', settings.theme_primary_foreground],
    ['--popover', settings.theme_card],
    ['--popover-foreground', settings.theme_card_foreground],
  ];

  for (const [name, hex] of vars) {
    if (hex) {
      const hsl = hexToHSL(hex);
      if (hsl) root.style.setProperty(name, hsl);
    }
  }

  // Apply body background and text directly for non-var fallbacks
  if (settings.theme_background) {
    body.style.backgroundColor = settings.theme_background;
  }
  if (settings.theme_foreground) {
    body.style.color = settings.theme_foreground;
  }

  // Fonts
  if (settings.theme_font_body) {
    loadGoogleFont(settings.theme_font_body);
    const fontVal = FONT_MAP[settings.theme_font_body] || ("'" + settings.theme_font_body + "', sans-serif");
    body.style.fontFamily = fontVal;
  }
  if (settings.theme_font_heading) {
    loadGoogleFont(settings.theme_font_heading);
    const headingFont = FONT_MAP[settings.theme_font_heading] || ("'" + settings.theme_font_heading + "', sans-serif");
    let style = document.getElementById('theme-heading-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'theme-heading-style';
      document.head.appendChild(style);
    }
    style.textContent = 'h1,h2,h3,h4,h5,h6{font-family:' + headingFont + '!important}';
  }
}

const THEME_CACHE_KEY = 'schoolpix_theme';

// Apply cached theme immediately (before React renders) to prevent flash
try {
  const cached = localStorage.getItem(THEME_CACHE_KEY);
  if (cached) applyTheme(JSON.parse(cached));
} catch (e) {
  // Corrupt/missing cache — safe to ignore, but surface for debugging.
  logError('ThemeContext.loadCache', e);
}

export function ThemeProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    try {
      const cached = localStorage.getItem(THEME_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      logError('ThemeContext.readCache', e);
      return null;
    }
  });
  const [themeReady, setThemeReady] = useState(() => {
    try {
      return !!localStorage.getItem(THEME_CACHE_KEY);
    } catch (e) {
      logError('ThemeContext.checkCache', e);
      return false;
    }
  });

  useEffect(() => {
    fetch(API + '/site-settings')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          setSettings(d);
          applyTheme(d);
          try {
            localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(d));
          } catch (e) {
            logError('ThemeContext.writeCache', e);
          }
        }
      })
      .catch((e) => logError('ThemeContext.fetchTheme', e))
      .finally(() => setThemeReady(true));
  }, []);

  const refreshTheme = useCallback(() => {
    fetch(API + '/site-settings')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          setSettings(d);
          applyTheme(d);
          try {
            localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(d));
          } catch (e) {
            logError('ThemeContext.refreshCache', e);
          }
        }
      })
      .catch((e) => logError('ThemeContext.refreshTheme', e));
  }, []);

  const value = useMemo(() => ({
    settings,
    refreshTheme,
    applyTheme,
  }), [settings, refreshTheme]);

  if (!themeReady) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#09090b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 24, height: 24, border: '2px solid #27272a', borderTopColor: '#a1a1aa', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const AVAILABLE_FONTS = Object.keys(FONT_MAP);
